import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { CURRICULUM } from './data/curriculum';
import { LessonStep } from './types';
import { useLearningProgress } from './hooks/useLearningProgress';
import Step1Introduction from './steps/Step1Introduction';
import Step2RepeatRule from './steps/Step2RepeatRule';
import Step3SeriesListening from './steps/Step3SeriesListening';
import Step4SeriesSpeaking from './steps/Step4SeriesSpeaking';
import Step5CalculatorInput from './steps/Step5CalculatorInput';
import Step6Commutative from './steps/Step6Commutative';
import Step7MixedReview from './steps/Step7MixedReview';
import LevelProgressBar from './components/LevelProgressBar';
import StartScreen from './components/StartScreen';
import { motion } from 'framer-motion';

function getNextStep(current: LessonStep): LessonStep | null {
  const order: LessonStep[] = [1, 2, 3, 4, 5, 6, 7];
  const idx = order.indexOf(current);
  return idx < order.length - 1 ? (order[idx + 1] as LessonStep) : null;
}

const StrategyLearningPage: React.FC = () => {
  const { user } = useAuth();
  const userId = user?.userId || '';
  const { progress, isLoading, error, saveProgress } = useLearningProgress(userId);

  const [currentLevel, setCurrentLevel] = useState<number>(1);
  const [currentStep, setCurrentStep] = useState<LessonStep>(1);
  const [isModuleComplete, setIsModuleComplete] = useState(false);
  const [showStartScreen, setShowStartScreen] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [step7Key, setStep7Key] = useState(0);

  // Initialize state from persisted progress
  useEffect(() => {
    if (!isLoading && !initialized) {
      if (progress) {
        if (progress.status === 'completed') {
          setIsModuleComplete(true);
        } else {
          setCurrentLevel(progress.currentLevel);
          // Always start from Step 1 of the current level — resuming mid-lesson
          // is confusing for young students since steps build on each other
          setCurrentStep(1);
        }
      } else {
        // First visit — create initial progress
        saveProgress({
          currentLevel: 1,
          currentStep: 1,
          levelHistory: {},
          status: 'in_progress',
        }).catch(() => {
          setInitError('Failed to initialize learning progress');
        });
      }
      setInitialized(true);
    }
  }, [isLoading, progress, initialized, saveProgress]);

  const handleStepComplete = useCallback(() => {
    const nextStep = getNextStep(currentStep);
    if (nextStep) {
      setCurrentStep(nextStep);
      saveProgress({ currentLevel, currentStep: nextStep });
    }
  }, [currentStep, currentLevel, saveProgress]);

  const handleMixedReviewComplete = useCallback(
    (passed: boolean, attempts: number, accuracy: number) => {
      if (passed) {
        const updatedHistory = {
          ...(progress?.levelHistory || {}),
          [currentLevel]: {
            attempts,
            accuracy,
            completedAt: new Date().toISOString(),
          },
        };

        if (currentLevel === 11) {
          // Module complete
          setIsModuleComplete(true);
          saveProgress({
            status: 'completed',
            levelHistory: updatedHistory,
          });
        } else {
          // Advance to next level
          const nextLevel = currentLevel + 1;
          setCurrentLevel(nextLevel);
          setCurrentStep(1);
          saveProgress({
            currentLevel: nextLevel,
            currentStep: 1,
            levelHistory: updatedHistory,
          });
        }
      }
      // Fail case is handled internally by Step7MixedReview (retry/restart)
    },
    [currentLevel, progress, saveProgress]
  );

  const handleRetryMixedReview = useCallback(() => {
    setStep7Key((prev) => prev + 1);
  }, []);

  const handleJumpTo = useCallback(
    (level: number, step: LessonStep) => {
      setCurrentLevel(level);
      setCurrentStep(step);
      setShowStartScreen(false);
    },
    []
  );

  if (isLoading || !initialized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-100 via-purple-50 to-indigo-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white p-8 rounded-2xl shadow-lg text-center"
        >
          <div className="animate-spin h-12 w-12 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading your learning session...</p>
        </motion.div>
      </div>
    );
  }

  if (error || initError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-100 via-purple-50 to-indigo-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 rounded-2xl shadow-lg text-center max-w-md"
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Something went wrong</h2>
          <p className="text-gray-600 mb-6">{error || initError}</p>
          <button
            onClick={() => (window.location.href = '/')}
            className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Back to Dashboard
          </button>
        </motion.div>
      </div>
    );
  }

  if (isModuleComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-100 via-emerald-50 to-teal-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white p-10 rounded-2xl shadow-lg text-center max-w-md"
        >
          <div className="text-6xl mb-4">&#127942;</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-3">Module Complete!</h2>
          <p className="text-gray-600 mb-8">
            You've mastered all 11 levels of Addition Within 10. Great work!
          </p>
          <button
            onClick={() => (window.location.href = '/')}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
          >
            Back to Dashboard
          </button>
        </motion.div>
      </div>
    );
  }

  if (showStartScreen) {
    if (!progress) {
      // Progress still being created (first visit) — show loading
      return (
        <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
          <div className="animate-spin h-12 w-12 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
        </div>
      );
    }
    return (
      <StartScreen
        progress={progress}
        onContinue={() => setShowStartScreen(false)}
        onJumpTo={handleJumpTo}
      />
    );
  }

  const levelData = CURRICULUM[currentLevel - 1];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-purple-50 to-indigo-100 flex flex-col">
      <LevelProgressBar level={currentLevel} step={currentStep} totalLevels={11} />

      <div className="flex-1 flex items-center justify-center p-4">
        {currentStep === 1 && (
          <Step1Introduction level={levelData} onComplete={handleStepComplete} />
        )}
        {currentStep === 2 && (
          <Step2RepeatRule level={levelData} onComplete={handleStepComplete} />
        )}
        {currentStep === 3 && (
          <Step3SeriesListening level={levelData} onComplete={handleStepComplete} />
        )}
        {currentStep === 4 && (
          <Step4SeriesSpeaking level={levelData} onComplete={handleStepComplete} />
        )}
        {currentStep === 5 && (
          <Step5CalculatorInput level={levelData} onComplete={handleStepComplete} />
        )}
        {currentStep === 6 && (
          <Step6Commutative level={levelData} onComplete={handleStepComplete} />
        )}
        {currentStep === 7 && (
          <Step7MixedReview
            key={step7Key}
            level={levelData}
            onComplete={handleMixedReviewComplete}
            onRetry={handleRetryMixedReview}
          />
        )}
      </div>
    </div>
  );
};

export default StrategyLearningPage;
